import { supabase } from '../lib/supabase';

const STATUS_MAP = {
  planned: 'scheduled',
  confirmed: 'scheduled',
  en_route: 'in_progress',
  on_site: 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled',
};

const SERVICE_ICONS = [
  { match: /fuite|eau|robinet/i, icon: 'Droplet' },
  { match: /chauff|chaudière|sanitaire/i, icon: 'Flame' },
  { match: /débouch|canalis|évacuation/i, icon: 'Waves' },
];

const WARRANTY_MONTHS = [
  { match: /débouch/i, months: 3 },
  { match: /fuite/i, months: 12 },
  { match: /install/i, months: 24 },
  { match: /dépann/i, months: 6 },
];

const formatPrice = (amount) => {
  if (amount == null || amount === '') return '—';
  const value = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

const getServiceIcon = (serviceType) => {
  const type = serviceType || '';
  const found = SERVICE_ICONS.find(({ match }) => match.test(type));
  return found?.icon || 'Wrench';
};

const getWarrantyMonths = (serviceType) => {
  const type = serviceType || '';
  const found = WARRANTY_MONTHS.find(({ match }) => match.test(type));
  return found?.months || 6;
};

const splitFullName = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const parseAddress = (address = '') => {
  if (!address) return { address: '', postalCode: '', city: '' };

  const postalMatch = address.match(/,\s*(\d{5})\s+(.+)$/);
  if (postalMatch) {
    return {
      address: address.replace(/,\s*\d{5}\s+.+$/, '').trim(),
      postalCode: postalMatch[1],
      city: postalMatch[2].trim(),
    };
  }

  return { address, postalCode: '', city: '' };
};

const buildDateTime = (date, time) => {
  if (!date) return null;
  const timePart = time ? String(time).slice(0, 5) : '00:00';
  return `${date}T${timePart}:00`;
};

const formatDuration = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate - startDate;
  if (diffMs <= 0) return null;

  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}min`;
};

const extractPhotos = (appointment) => {
  const photos = [];

  if (Array.isArray(appointment?.photosclient)) {
    appointment.photosclient.forEach((url, index) => {
      if (typeof url === 'string' && url) {
        photos.push({
          url,
          alt: `Photo client ${index + 1}`,
          label: `Photo ${index + 1}`,
        });
      }
    });
  }

  const resultPhotos = appointment?.appointment_result;
  if (Array.isArray(resultPhotos)) {
    resultPhotos.forEach((entry, index) => {
      if (Array.isArray(entry?.images)) {
        entry.images.forEach((url, imageIndex) => {
          if (typeof url === 'string' && url) {
            photos.push({
              url,
              alt: `Photo intervention ${index + 1}`,
              label: `Intervention ${imageIndex + 1}`,
            });
          }
        });
      }
    });
  }

  if (appointment?.photos?.before) {
    appointment.photos.before.forEach((url, index) => {
      photos.push({ url, alt: 'Avant intervention', label: `Avant ${index + 1}` });
    });
  }

  if (appointment?.photos?.after) {
    appointment.photos.after.forEach((url, index) => {
      photos.push({ url, alt: 'Après intervention', label: `Après ${index + 1}` });
    });
  }

  return photos;
};

const mapAppointmentToService = (appointment, technicianName) => {
  const status = STATUS_MAP[appointment?.status] || appointment?.status;
  const serviceName = appointment?.service_type || appointment?.Service?.name || 'Service';
  const isCompleted = appointment?.status === 'completed';
  const scheduledAt = buildDateTime(appointment?.scheduled_date, appointment?.scheduled_time_start);
  const duration = formatDuration(appointment?.actual_arrival_time, appointment?.completion_time);

  return {
    id: appointment.id,
    reference: appointment.reference,
    title: serviceName,
    status,
    date: scheduledAt || appointment?.created_at,
    completionDate: appointment?.completion_time || null,
    address: appointment?.customer_address || '',
    technician: technicianName || 'Non assigné',
    price: formatPrice(appointment?.price_total),
    icon: getServiceIcon(serviceName),
    equipment: appointment?.Service?.equipment?.name || serviceName,
    problem: appointment?.notes || '—',
    duration: duration || (status === 'scheduled' ? 'À définir' : '—'),
    warranty: isCompleted,
    notes: appointment?.notes || '',
    photos: extractPhotos(appointment),
    rawStatus: appointment?.status,
    serviceType: serviceName,
  };
};

const buildWarranties = (appointments) => {
  return appointments
    .filter((apt) => apt.status === 'completed')
    .map((apt) => {
      const serviceName = apt.service_type || apt.Service?.name || 'Service';
      const serviceDate = apt.completion_time || apt.scheduled_date;
      const months = getWarrantyMonths(serviceName);
      const expiration = new Date(serviceDate);
      expiration.setMonth(expiration.getMonth() + months);

      return {
        id: apt.id,
        reference: apt.reference,
        serviceName,
        serviceDate: serviceDate?.split?.('T')?.[0] || serviceDate,
        expirationDate: expiration.toISOString().split('T')[0],
        coverage: [
          'Pièces de rechange fournies',
          'Main d\'œuvre pour réparation',
          'Déplacement du technicien',
        ],
      };
    })
    .filter((warranty) => new Date(warranty.expirationDate) >= new Date());
};

const buildDocuments = (appointments) => {
  const documents = [];

  appointments.forEach((apt) => {
    if (apt.status !== 'completed') return;

    const docDate = (apt.completion_time || apt.scheduled_date || apt.created_at)?.split?.('T')?.[0]
      || apt.scheduled_date;

    documents.push({
      id: `invoice-${apt.id}`,
      appointmentId: apt.id,
      name: `Facture ${apt.reference}`,
      type: 'invoice',
      date: docDate,
      size: 245000,
      format: 'PDF',
    });

    documents.push({
      id: `warranty-${apt.id}`,
      appointmentId: apt.id,
      name: `Certificat de garantie - ${apt.service_type || 'Service'}`,
      type: 'warranty',
      date: docDate,
      size: 156000,
      format: 'PDF',
    });

    if (apt.appointment_result || apt.notes) {
      documents.push({
        id: `report-${apt.id}`,
        appointmentId: apt.id,
        name: `Rapport d'intervention - ${apt.service_type || 'Service'}`,
        type: 'report',
        date: docDate,
        size: 389000,
        format: 'PDF',
      });
    }
  });

  return documents;
};

const buildPaymentMethods = (appointments) => {
  const methods = [];
  const seen = new Set();

  appointments.forEach((apt) => {
    if (!apt.payment_method || apt.payment_status !== 'succeeded') return;

    const key = `${apt.payment_method}-${apt.stripe_customer_id || 'default'}`;
    if (seen.has(key)) return;
    seen.add(key);

    methods.push({
      id: apt.id,
      type: apt.payment_method === 'card' ? 'visa' : apt.payment_method,
      last4: '****',
      expiryDate: null,
      isDefault: methods.length === 0,
    });
  });

  return methods;
};

const mapNotificationType = (type = '') => {
  if (type.includes('complaint')) return 'complaint';
  if (type.includes('feedback')) return 'feedback';
  if (type.includes('sms')) return 'sms';
  if (type.includes('call')) return 'call';
  return 'email';
};

const buildUserData = (user, profile, appointments, metadata = {}) => {
  const split = splitFullName(profile?.full_name || metadata?.full_name || '');
  const latestAppointment = appointments?.[0];

  const firstName = (
    metadata?.first_name
    || split.firstName
    || ''
  ).trim();
  const lastName = (
    metadata?.last_name
    || split.lastName
    || ''
  ).trim();

  const fullName = (
    [firstName, lastName].filter(Boolean).join(' ')
    || metadata?.full_name
    || profile?.full_name
    || ''
  ).trim();

  const siretFromAppointment = String(latestAppointment?.siret || '').replace(/\s/g, '');
  const siret = String(metadata?.siret || siretFromAppointment || '').replace(/\s/g, '');
  const companyName = (
    metadata?.company_name
    || latestAppointment?.companyName
    || latestAppointment?.company_name
    || ''
  ).trim();

  const customerType =
    metadata?.customer_type
    || (siret || companyName ? 'business' : 'individual');

  return {
    fullName,
    firstName: firstName || splitFullName(fullName).firstName,
    lastName: lastName || splitFullName(fullName).lastName,
    email: profile?.email || user?.email || '',
    phone: profile?.phone || metadata?.phone || latestAppointment?.customer_phone || '',
    customerType: customerType === 'business' ? 'business' : 'individual',
    companyName,
    siret,
    emailNotifications: metadata?.email_notifications ?? true,
    smsNotifications: metadata?.sms_notifications ?? true,
    autoReminders: metadata?.auto_reminders ?? true,
    promotionalEmails: metadata?.promotional_emails ?? false,
  };
};

const mergeBookingPrefill = (existing = {}, prefill = {}) => {
  const merged = { ...existing };
  Object.entries(prefill).forEach(([key, value]) => {
    if (value != null && value !== '' && !merged[key]) {
      merged[key] = value;
    }
  });
  return merged;
};

export const customerPortalService = {
  async getBookingPrefillData(user, profile) {
    if (!user?.id) return null;

    const appointments = await this.getClientAppointments(user.id, user.email);
    const userData = buildUserData(user, profile, appointments, user?.user_metadata || {});
    const latestAppointment = appointments?.[0];
    const parsedAddress = parseAddress(
      user?.user_metadata?.address || latestAppointment?.customer_address || ''
    );
    const nameParts = splitFullName(userData.fullName);

    return {
      fullName: userData.fullName,
      firstName: nameParts.firstName,
      lastName: nameParts.lastName,
      email: userData.email,
      phone: userData.phone,
      streetLine: parsedAddress.address,
      postcode: user?.user_metadata?.postal_code || parsedAddress.postalCode,
      city: user?.user_metadata?.city || parsedAddress.city,
      lat: latestAppointment?.customer_latitude || user?.user_metadata?.lat || null,
      lng: latestAppointment?.customer_longitude || user?.user_metadata?.lng || null,
      accessInfo: latestAppointment?.accessInfo || user?.user_metadata?.access_info || '',
      createAccount: false,
      isLoggedIn: true,
    };
  },

  mergeBookingPrefill,

  async checkEmailExists(email) {
    const normalized = email?.trim()?.toLowerCase();
    if (!normalized) return false;

    const { data, error } = await supabase
      .from('user_profiles')
      .select('id')
      .ilike('email', normalized)
      .maybeSingle();

    if (error) {
      console.warn('Vérification email:', error);
      return false;
    }

    return Boolean(data?.id);
  },

  isEmailAlreadyRegisteredError(error) {
    const message = (error?.message || String(error || '')).toLowerCase();
    return (
      message.includes('already') ||
      message.includes('exists') ||
      message.includes('registered') ||
      message.includes('duplicate') ||
      message.includes('unique')
    );
  },
  async getClientAppointments(userId, userEmail) {
    const selectWithJoins = `
      *,
      Service:Service(id, name, equipment(id, name)),
      technician:user_profiles!appointments_technician_fkey(id, full_name, email, phone)
    `;

    const fetchAppointments = async (filterFn) => {
      let query = supabase
        .from('appointments')
        .select(selectWithJoins)
        .order('scheduled_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      query = filterFn(query);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    };

    const fetchAppointmentsSimple = async (filterFn) => {
      let query = supabase
        .from('appointments')
        .select('*')
        .order('scheduled_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      query = filterFn(query);

      const { data, error } = await query;
      if (error) throw error;

      return Promise.all(
        (data || []).map(async (apt) => {
          let technician = null;
          if (apt.technician) {
            const { data: techData } = await supabase
              .from('user_profiles')
              .select('id, full_name, email, phone')
              .eq('id', apt.technician)
              .single();
            technician = techData;
          }
          return { ...apt, technician };
        })
      );
    };

    try {
      const byClient = await fetchAppointments((q) => q.eq('client', userId));
      if (byClient.length > 0) return byClient;

      if (!userEmail) return [];

      return await fetchAppointments((q) => q.eq('customer_email', userEmail));
    } catch (joinError) {
      console.warn('Jointure appointments échouée, repli sans jointure:', joinError);

      const byClient = await fetchAppointmentsSimple((q) => q.eq('client', userId));
      if (byClient.length > 0) return byClient;

      if (!userEmail) return [];

      return fetchAppointmentsSimple((q) => q.eq('customer_email', userEmail));
    }
  },

  async getCommunications(appointmentIds) {
    if (!appointmentIds?.length) return [];

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .in('appointment_id', appointmentIds)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getPortalData(user, profile) {
    const appointments = await this.getClientAppointments(user?.id, user?.email);
    const appointmentIds = appointments.map((apt) => apt.id);
    const notifications = await this.getCommunications(appointmentIds);

    const referenceById = Object.fromEntries(
      appointments.map((apt) => [apt.id, apt.reference])
    );

    const serviceHistory = appointments.map((apt) =>
      mapAppointmentToService(apt, apt.technician?.full_name)
    );

    const communications = notifications.map((notif) => ({
      id: notif.id,
      type: mapNotificationType(notif.notification_type),
      subject: notif.title,
      message: notif.message,
      status: notif.is_read ? 'closed' : 'pending',
      date: notif.created_at,
      reference: referenceById[notif.appointment_id] || null,
    }));

    const metadata = user?.user_metadata || {};

    return {
      userData: buildUserData(user, profile, appointments, metadata),
      serviceHistory,
      documents: buildDocuments(appointments),
      warranties: buildWarranties(appointments),
      paymentMethods: buildPaymentMethods(appointments),
      communications,
    };
  },

  async updateUserSettings(userId, formData, currentUser) {
    const firstName = formData.firstName?.trim() || '';
    const lastName = formData.lastName?.trim() || '';
    const fullName = formData.fullName?.trim()
      || `${firstName} ${lastName}`.trim();
    // Type de personne figé : on conserve l'existant (metadata), jamais un switch depuis le formulaire
    const existingType = currentUser?.user_metadata?.customer_type;
    const existingSiret = String(currentUser?.user_metadata?.siret || '').replace(/\s/g, '');
    const existingCompany = String(currentUser?.user_metadata?.company_name || '').trim();
    let customerType = 'individual';
    if (existingType === 'business' || existingType === 'individual') {
      customerType = existingType;
    } else if (existingSiret || existingCompany) {
      customerType = 'business';
    }
    const companyName = customerType === 'business' ? (formData.companyName?.trim() || '') : '';
    const siret = customerType === 'business'
      ? String(formData.siret || '').replace(/\s/g, '')
      : '';
    // Email = clé d'identité : jamais modifiable depuis le profil
    const email = currentUser?.email;

    const profileUpdate = {
      full_name: fullName,
      phone: formData.phone,
      email,
    };

    const { data: updatedRows, error: updateError } = await supabase
      .from('user_profiles')
      .update(profileUpdate)
      .eq('id', userId)
      .select();

    let profileData = updatedRows?.[0] || null;

    if (updateError || !profileData) {
      const { data: insertedProfile, error: insertError } = await supabase
        .from('user_profiles')
        .insert({
          id: userId,
          full_name: fullName,
          phone: formData.phone,
          email,
          role: currentUser?.user_metadata?.role || 'client',
          is_active: true,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (insertError) {
        console.warn('Impossible de mettre à jour user_profiles:', insertError);
      } else {
        profileData = insertedProfile;
      }
    }

    const authPayload = {
      data: {
        full_name: fullName,
        first_name: firstName,
        last_name: lastName,
        phone: formData.phone,
        customer_type: customerType,
        company_name: companyName,
        siret,
        email_notifications: formData.emailNotifications,
        sms_notifications: formData.smsNotifications,
        auto_reminders: formData.autoReminders,
        promotional_emails: formData.promotionalEmails,
      },
    };

    const { data: authData, error: authError } = await supabase.auth.updateUser(authPayload);
    if (authError) {
      throw new Error(authError.message || 'Erreur lors de la mise à jour du compte');
    }

    if (!profileData && !authData?.user) {
      throw new Error('La mise à jour du profil a échoué. Vérifiez vos permissions.');
    }

    return { profileData, user: authData?.user };
  },

  async updateOwnPassword(password) {
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) {
      throw new Error(error.message || 'Impossible de changer le mot de passe');
    }
    return data;
  },
};
